/**
 * V111-7-5 / V111-7-7 (D-111-14; ADR-0001 completion for proposals).
 *
 * Scanner findings are embedded and matched against EXISTING register rows
 * (legislation / case_law vectors beside the risk items, register-vector-
 * service) with an LLM re-rank as the precision stage. The decision —
 * "variant of X" (file an UPDATE proposal against X) vs "similar to X"
 * (indicator for the reviewer) vs "new" — is driven by TUNABLE CONTENT
 * (migration 742, `variant_match_settings`), never code constants: the
 * thresholds decide duplicate-flood vs missed-amendment, so they must move
 * without a release.
 *
 * Deterministic spine, LLM precision: the vector query produces candidates;
 * the re-rank confirms sameness; a gateway refusal falls back to the pure
 * embedding threshold (the questionnaire-matcher precedent) — degraded,
 * never blocked.
 */
import { sqlIn } from '../utils/sql-in.js';
import { defineTemplate, field } from '../egress/data-classes.js';
import { CASE_LAW_VECTOR_PREFIX, LEGISLATION_VECTOR_PREFIX, queryIndexForText, } from './register-vector-service.js';
/**
 * Code fallback for a database where migration 742 has not run (or the row
 * was removed). The CONTENT row with the highest version wins when present.
 */
export const DEFAULT_VARIANT_SETTINGS = {
    version: 'v111.1-code-default',
    variant_min_score: 0.8,
    candidate_min_score: 0.6,
    top_k: 5,
    rerank: true,
};
export async function getVariantMatchSettings(db) {
    try {
        const row = (await db.query(`SELECT version, settings_json FROM variant_match_settings ORDER BY created_at DESC, version DESC LIMIT 1`)).results[0];
        if (!row)
            return DEFAULT_VARIANT_SETTINGS;
        const parsed = JSON.parse(row.settings_json);
        return {
            version: row.version,
            variant_min_score: clamp01(parsed.variant_min_score, DEFAULT_VARIANT_SETTINGS.variant_min_score),
            candidate_min_score: clamp01(parsed.candidate_min_score, DEFAULT_VARIANT_SETTINGS.candidate_min_score),
            top_k: Number.isInteger(parsed.top_k) && parsed.top_k > 0 ? parsed.top_k : DEFAULT_VARIANT_SETTINGS.top_k,
            rerank: parsed.rerank !== false,
        };
    }
    catch {
        return DEFAULT_VARIANT_SETTINGS;
    }
}
function clamp01(v, fallback) {
    return typeof v === 'number' && v >= 0 && v <= 1 ? v : fallback;
}
/** The target types the variant detector covers (prefix ↔ table). */
const VARIANT_TARGETS = {
    legislation: {
        prefix: LEGISLATION_VECTOR_PREFIX,
        table: 'legislation',
        idCol: 'legislation_id',
        titleCol: 'title',
        textSql: `title || ' (' || COALESCE(bill_id, '') || '). ' || short_title || '. ' || COALESCE(description, '') || ' Jurisdiction: ' || jurisdiction`,
    },
    case_law: {
        prefix: CASE_LAW_VECTOR_PREFIX,
        table: 'case_law',
        idCol: 'case_id',
        titleCol: 'case_name',
        textSql: `case_name || '. ' || COALESCE(summary, '') || ' Court: ' || court || '. Jurisdiction: ' || jurisdiction`,
    },
};
export function variantDetectionCovers(targetType) {
    return targetType in VARIANT_TARGETS;
}
/** The matched row's human title, so the queue names it instead of showing an id. */
export async function variantTargetTitle(db, targetType, targetId) {
    const t = VARIANT_TARGETS[targetType];
    if (!t)
        return null;
    const row = (await db.query(`SELECT ${t.titleCol} AS title FROM ${t.table} WHERE ${t.idCol} = ?`, [targetId])).results[0];
    return row?.title ?? null;
}
export const VARIANT_RERANK_INSTRUCTIONS = [
    'You compare an incoming research finding against existing register rows.',
    'The input JSON carries `finding` (title + excerpt) and `candidates` (id + text).',
    'Return `match_id` as the id of the candidate that describes the SAME legal',
    'instrument, case or event as the finding (an amendment, a later stage, or a',
    'new development OF that instrument still counts as the same), or null when',
    'every candidate is a different instrument. Return `confidence` in [0, 1].',
    'Pick only from the listed candidate ids; never invent one.',
].join(' ');
const RERANK_TEMPLATE = defineTemplate('research.match_rerank', 'v1', VARIANT_RERANK_INSTRUCTIONS, []);
/**
 * Embed the finding text, query the shared index, and decide variant/similar/
 * new for `targetType`. Pure reads; no proposal side effects.
 */
export async function matchFindingToExisting(db, llm, index, input, settings) {
    const none = {
        decision: 'none', matched_target_id: null, score: null, method: 'embedding',
        threshold_version: settings.version, candidates: [], similar_register_items: [],
    };
    const target = VARIANT_TARGETS[input.targetType];
    if (!target)
        return none;
    // One query serves both prefixed targets and bare risk-item ids.
    const matches = await queryIndexForText(llm, index, input.text, Math.max(settings.top_k * 4, 20));
    if (matches.length === 0)
        return none;
    const candidates = matches
        .filter((m) => m.id.startsWith(target.prefix) && m.score >= settings.candidate_min_score)
        .slice(0, settings.top_k)
        .map((m) => ({ target_id: m.id.slice(target.prefix.length), score: m.score }));
    const similarRegisterItems = matches
        .filter((m) => !m.id.includes(':') && m.score >= settings.candidate_min_score)
        .slice(0, settings.top_k)
        .map((m) => ({ target_id: m.id, score: m.score }));
    if (candidates.length === 0) {
        return { ...none, similar_register_items: similarRegisterItems };
    }
    // Re-rank (precision stage): the LLM says which candidate, if any, is the
    // SAME instrument/case. A refusal falls back to the embedding threshold.
    if (settings.rerank) {
        const confirmed = await rerankSameInstrument(db, llm, input.text, target, candidates);
        if (confirmed !== 'refused') {
            if (confirmed) {
                const c = candidates.find((x) => x.target_id === confirmed);
                return {
                    decision: 'variant', matched_target_id: c.target_id, score: c.score, method: 'rerank',
                    threshold_version: settings.version, candidates, similar_register_items: similarRegisterItems,
                };
            }
            // The re-rank looked and said "none of these" — similar at best.
            return {
                decision: 'similar', matched_target_id: candidates[0].target_id, score: candidates[0].score,
                method: 'rerank', threshold_version: settings.version, candidates, similar_register_items: similarRegisterItems,
            };
        }
    }
    const top = candidates[0];
    return {
        decision: top.score >= settings.variant_min_score ? 'variant' : 'similar',
        matched_target_id: top.target_id,
        score: top.score,
        method: 'embedding',
        threshold_version: settings.version,
        candidates,
        similar_register_items: similarRegisterItems,
    };
}
/**
 * Returns the confirmed candidate id, null for "none of these", or 'refused'
 * when the gateway could not run the task.
 */
async function rerankSameInstrument(db, llm, findingText, target, candidates) {
    try {
        const ids = candidates.map((c) => c.target_id);
        const inIds = sqlIn(ids);
        const rows = (await db.query(`SELECT ${target.idCol} AS id, ${target.textSql} AS text FROM ${target.table} WHERE ${target.idCol} IN ${inIds.sql}`, inIds.params)).results;
        if (rows.length === 0)
            return null;
        const payload = JSON.stringify({
            finding: findingText.slice(0, 3_000),
            candidates: rows.map((x) => ({ id: x.id, text: x.text.slice(0, 600) })),
        });
        const r = await llm.run({
            task: 'research.match_rerank',
            template: RERANK_TEMPLATE,
            input: field('research.match_payload', payload),
            schemaHint: { match_id: 'candidate id or null', confidence: '0..1' },
        });
        if (r.status !== 'ok')
            return 'refused';
        const matchId = r.output.match_id;
        // Only an id from the candidate set counts (the questionnaire-matcher rule).
        if (matchId && ids.includes(matchId))
            return matchId;
        return null;
    }
    catch {
        return 'refused';
    }
}
//# sourceMappingURL=variant-match-service.js.map